import React, { useState, useMemo, useEffect, useRef } from "react";
import Prism from "prismjs";
import { Accordion } from "aible";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-css";
import "prismjs/components/prism-json";
import "prismjs/components/prism-python";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-yaml";
import "prismjs/components/prism-markdown";
import "prismjs/themes/prism.css";
import styles from "./ToolCallDisplay.module.css";
import { isEnabled } from "@/utils/featureFlags";
const TOOL_META = {
    read: { label: "Read File", icon: "📖" },
    write: { label: "Write File", icon: "✏️" },
    edit: { label: "Edit File", icon: "📝" },
    shell: { label: "Shell", icon: "⚡" },
    bash: { label: "Bash", icon: "⚡" },
    glob: { label: "Find Files", icon: "🔍" },
    grep: { label: "Search", icon: "🔎" },
    ls: { label: "List Files", icon: "📁" },
    web_search: { label: "Web Search", icon: "🌐" },
    database_query: { label: "Database Query", icon: "🗄️" },
    data_analysis: { label: "Data Analysis", icon: "📊" },
    log_search: { label: "Searching Logs", icon: "📋" },
    ci_status: { label: "CI/CD Pipeline", icon: "🚀" },
    health_check: { label: "Health Check", icon: "💚" },
    browser: { label: "Browser", icon: "🌐" },
    memory_search: { label: "Searching Memory", icon: "🧠" }
};
export function getToolMeta(name) {
    if (TOOL_META[name])
        return TOOL_META[name];
    const label = name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return { label, icon: "🔧" };
}
/** Map file extension to Prism language key */
function getPrismLanguage(filePath) {
    const ext = filePath.split(".").pop()?.toLowerCase() ?? "";
    const map = {
        ts: "typescript",
        tsx: "typescript",
        js: "javascript",
        jsx: "javascript",
        py: "python",
        rb: "ruby",
        go: "go",
        rs: "rust",
        java: "java",
        html: "markup",
        htm: "markup",
        xml: "markup",
        svg: "markup",
        css: "css",
        scss: "css",
        json: "json",
        yaml: "yaml",
        yml: "yaml",
        md: "markdown",
        sql: "sql",
        sh: "bash",
        bash: "bash",
        zsh: "bash",
        toml: "yaml"
    };
    return map[ext] ?? "markup";
}
function guessLanguageLabel(filePath) {
    const ext = filePath.split(".").pop()?.toLowerCase() ?? "";
    const map = {
        ts: "TypeScript",
        tsx: "TSX",
        js: "JavaScript",
        jsx: "JSX",
        py: "Python",
        rb: "Ruby",
        go: "Go",
        rs: "Rust",
        java: "Java",
        html: "HTML",
        css: "CSS",
        scss: "SCSS",
        json: "JSON",
        yaml: "YAML",
        yml: "YAML",
        md: "Markdown",
        sql: "SQL",
        sh: "Shell",
        bash: "Shell",
        xml: "XML",
        toml: "TOML"
    };
    return map[ext] ?? "";
}
function getFileExtension(filePath) {
    return filePath.split(".").pop()?.toLowerCase() ?? "";
}
function getFileName(filePath) {
    return filePath.split("/").pop() ?? filePath;
}
function formatInputSummary(input) {
    const skip = new Set(["content", "path", "file_path", "filePath"]);
    const entries = Object.entries(input).filter(([key]) => !skip.has(key));
    if (entries.length === 0)
        return null;
    return entries
        .map(([key, value]) => {
        const v = typeof value === "string" ? value : JSON.stringify(value, null, 2);
        return `${key}: ${v}`;
    })
        .join("\n");
}
function isPreviewable(filePath) {
    const ext = getFileExtension(filePath);
    return ["html", "htm", "svg", "md"].includes(ext);
}
/** Simple markdown to HTML for preview (no dependencies) */
function markdownToHtml(md) {
    let html = md
        // Headers
        .replace(/^### (.+)$/gm, "<h3>$1</h3>")
        .replace(/^## (.+)$/gm, "<h2>$1</h2>")
        .replace(/^# (.+)$/gm, "<h1>$1</h1>")
        // Bold & italic
        .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        // Code blocks
        .replace(/```[\w]*\n([\s\S]*?)```/g, "<pre><code>$1</code></pre>")
        // Inline code
        .replace(/`([^`]+)`/g, "<code>$1</code>")
        // Links
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
        // Lists
        .replace(/^\- (.+)$/gm, "<li>$1</li>")
        .replace(/^\* (.+)$/gm, "<li>$1</li>")
        .replace(/^\d+\. (.+)$/gm, "<li>$1</li>")
        // Paragraphs (double newline)
        .replace(/\n\n/g, "</p><p>")
        // Single newlines to <br>
        .replace(/\n/g, "<br>");
    return `<!DOCTYPE html><html><head><style>
    body { font-family: -apple-system, system-ui, sans-serif; padding: 20px; line-height: 1.6; color: #1f2937; max-width: 800px; }
    h1, h2, h3 { color: #111827; margin-top: 1.5em; }
    code { background: #f3f4f6; padding: 2px 5px; border-radius: 3px; font-size: 0.9em; }
    pre { background: #f3f4f6; padding: 12px; border-radius: 6px; overflow-x: auto; }
    pre code { background: none; padding: 0; }
    li { margin: 4px 0; }
    a { color: #2563eb; }
  </style></head><body><p>${html}</p></body></html>`;
}
/** Highlighted code block using Prism */
const HighlightedCode = ({ code, language }) => {
    const codeRef = useRef(null);
    useEffect(() => {
        if (codeRef.current) {
            Prism.highlightElement(codeRef.current);
        }
    }, [code, language]);
    return (React.createElement("code", { ref: codeRef, className: `language-${language}` }, code));
};
/** Detect if output text looks like a file listing */
function isFileListing(text, toolName) {
    if (!text)
        return false;
    const fileListTools = new Set(["exec", "shell", "bash", "ls"]);
    if (!fileListTools.has(toolName))
        return false;
    const lines = text.trim().split("\n");
    if (lines.length < 2)
        return false;
    // Detect ls -la format: lines starting with permissions like "drwxr-xr-x" or "-rw-r--r--"
    const lsLaLines = lines.filter((l) => /^[d\-lrwxsStT]{10}/.test(l.trim()));
    if (lsLaLines.length / lines.length > 0.5)
        return true;
    // Detect simple ls format: one filename per line
    const fileNameish = lines.filter((l) => /^[\w\-\.]+$/.test(l.trim()));
    return fileNameish.length / lines.length > 0.7;
}
/** Parse filenames from ls output (handles both simple and ls -la format) */
function parseFileList(text) {
    const lines = text.trim().split("\n");
    const results = [];
    for (const line of lines) {
        const trimmed = line.trim();
        // Skip "total XX" line and . / .. entries
        if (/^total\s+\d+/.test(trimmed))
            continue;
        // ls -la format: "-rw-r--r-- 1 sandbox sandbox  866 Mar 21 21:15 simple.html"
        const lsLaMatch = trimmed.match(/^([d\-lrwxsStT]{10})\s+\d+\s+\S+\s+\S+\s+([\d,]+)\s+\w+\s+\d+\s+[\d:]+\s+(.+)$/);
        if (lsLaMatch) {
            const perms = lsLaMatch[1];
            const size = lsLaMatch[2];
            const name = lsLaMatch[3];
            if (name === "." || name === "..")
                continue;
            results.push({
                name,
                isDir: perms.startsWith("d"),
                size: formatFileSize(parseInt(size, 10))
            });
            continue;
        }
        // Simple format: just a filename
        if (/^[\w\-\.]+$/.test(trimmed) && trimmed !== "." && trimmed !== "..") {
            results.push({ name: trimmed, isDir: false });
        }
    }
    return results;
}
/** Format bytes to human readable */
function formatFileSize(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
/** Get an icon for a file based on extension */
function getFileIcon(fileName) {
    const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
    const icons = {
        html: "🌐",
        htm: "🌐",
        css: "🎨",
        js: "📜",
        ts: "📘",
        jsx: "⚛️",
        tsx: "⚛️",
        json: "📋",
        md: "📝",
        py: "🐍",
        rb: "💎",
        go: "🔷",
        rs: "🦀",
        java: "☕",
        sql: "🗄️",
        sh: "⚡",
        bash: "⚡",
        yaml: "⚙️",
        yml: "⚙️",
        toml: "⚙️",
        xml: "📄",
        svg: "🎨",
        txt: "📄",
        env: "🔒"
    };
    return icons[ext] ?? "📄";
}
/** Renders a file list — Preview buttons ask the agent to read the file */
const FileListView = ({ text, cwd }) => {
    const files = parseFileList(text);
    const basePath = cwd ?? "/sandbox/.openclaw/workspace";
    if (files.length === 0)
        return null;
    return (React.createElement("div", { className: styles.fileList },
        React.createElement("div", { className: styles.fileListHeader },
            React.createElement("span", { className: styles.fileListHeaderText },
                files.length,
                " items"),
            cwd && React.createElement("span", { className: styles.fileListHeaderPath }, cwd)),
        files.map((file, i) => {
            const fullPath = `${basePath}/${file.name}`;
            return React.createElement(FileListItem, { key: i, fileName: file.name, fullPath: fullPath, isDir: file.isDir, size: file.size });
        })));
};
/** Single file in the list */
const FileListItem = ({ fileName, fullPath, isDir, size }) => {
    return (React.createElement("div", { className: styles.fileListItem },
        React.createElement("span", { className: styles.fileListIcon }, isDir ? "📁" : getFileIcon(fileName)),
        React.createElement("span", { className: `${styles.fileListName} ${isDir ? styles.fileListDir : ""}` },
            fileName,
            isDir ? "/" : ""),
        size && React.createElement("span", { className: styles.fileListSize }, size),
        React.createElement("span", { className: styles.fileListPath, title: fullPath }, fullPath)));
};
const AIBLE_TOOL_PREFIX = "aible_";
const ToolCallFeedback = ({ feedback, updateFeedback }) => {
    const [showFeedback, setShowFeedback] = useState(Boolean(feedback));
    const [feedbackInput, setFeedbackInput] = useState("");
    if (!isEnabled("openshell-feedback")) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement("div", { className: styles.toolCallFeedbackWrapper },
        React.createElement("a", { onClick: () => {
                if (showFeedback) {
                    updateFeedback();
                }
                setShowFeedback(!showFeedback);
            } }, showFeedback ? "Clear Hint" : "Add Hint for Retry"),
        showFeedback && (React.createElement("div", { className: styles.toolCallFeedbackInput },
            React.createElement("input", { type: "text", autoComplete: "off", name: "feedback", value: feedbackInput, onChange: (e) => setFeedbackInput(e.target.value), onBlur: (e) => {
                    updateFeedback(e.target.value);
                } })))));
};
/** Renders a single tool call */
const ToolCallCard = ({ toolCall, toolCallsDisplayMode, feedback, updateFeedback }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [viewMode, setViewMode] = useState("code");
    const [previewAccepted, setPreviewAccepted] = useState(false);
    const [isAccordionOpen, setIsAccordionOpen] = useState(toolCallsDisplayMode === "Show full details");
    useEffect(() => {
        setIsAccordionOpen(toolCallsDisplayMode === "Show full details");
    }, [toolCallsDisplayMode]);
    if (!toolCall || !toolCall.name)
        return null;
    const { label, icon } = getToolMeta(toolCall.name);
    const isRunning = toolCall.status === "running";
    const isError = toolCall.status === "error";
    const isAibleToolCall = toolCall?.name?.startsWith(AIBLE_TOOL_PREFIX);
    // Extract file path from various possible input keys
    let filePath = (toolCall.input?.path ??
        toolCall.input?.file_path ??
        toolCall.input?.filePath ??
        toolCall.input?.file ??
        toolCall.input?.filename);
    // Fallback: try to extract path from the output/meta string
    // e.g. "from ~/.openclaw/workspace/simple.html" or "to ~/.openclaw/workspace/simple.html (866 chars)"
    if (!filePath && toolCall.output) {
        const pathMatch = toolCall.output.match(/(?:from|to)\s+([\S]+\.[\w]+)/);
        if (pathMatch)
            filePath = pathMatch[1];
    }
    const fileContent = toolCall.input?.content;
    const languageLabel = filePath ? guessLanguageLabel(filePath) : "";
    const prismLang = filePath ? getPrismLanguage(filePath) : "markup";
    const inputSummary = toolCall.input ? formatInputSummary(toolCall.input) : null;
    const displayContent = fileContent ?? toolCall.output ?? null;
    // Also grab resultText if available (the raw result, separate from meta)
    const resultText = toolCall.resultText;
    const resultDetails = toolCall.resultDetails;
    const lineCount = displayContent?.split("\n").length ?? 0;
    const isLong = lineCount > 12;
    const canPreview = filePath && isPreviewable(filePath) && !!displayContent;
    // Detect file listing output
    const rawText = resultText ?? toolCall.output ?? "";
    const showFileList = isFileListing(rawText, toolCall.name);
    const cwd = resultDetails?.cwd;
    const blobUrl = useMemo(() => {
        if (!canPreview || !displayContent)
            return null;
        const ext = getFileExtension(filePath);
        if (ext === "md") {
            // Convert markdown to HTML for preview
            const html = markdownToHtml(displayContent);
            const blob = new Blob([html], { type: "text/html" });
            return URL.createObjectURL(blob);
        }
        const mimeType = ext === "svg" ? "image/svg+xml" : "text/html";
        const blob = new Blob([displayContent], { type: mimeType });
        return URL.createObjectURL(blob);
    }, [displayContent, canPreview, filePath]);
    return (React.createElement("div", { className: `${styles.toolCallContainer} ${isRunning ? styles.running : ""} ${isError ? styles.error : ""}` },
        React.createElement(Accordion, { className: styles.toolCallAccordion, bodyClass: styles.toolCallAccordionBody, defaultShow: toolCallsDisplayMode === "Show full details", show: isAccordionOpen, onClick: () => setIsAccordionOpen((isOpen) => !isOpen), noBot: true, header: React.createElement("div", { className: styles.toolCallHeader },
                React.createElement("span", { className: styles.toolCallIcon }, icon),
                React.createElement("span", { className: styles.toolCallName }, label),
                filePath && (React.createElement("span", { className: styles.toolCallFilePath, title: filePath }, getFileName(filePath))),
                languageLabel && React.createElement("span", { className: styles.toolCallBadge }, languageLabel),
                isRunning && React.createElement("span", { className: styles.toolCallSpinner }),
                toolCall.status === "complete" && React.createElement("span", { className: styles.toolCallCheck }, "\u2713"),
                isError && React.createElement("span", { className: styles.toolCallErrorIcon }, "\u2715")) },
            filePath && filePath.includes("/") && React.createElement("div", { className: styles.toolCallFullPath }, filePath),
            inputSummary && (React.createElement("div", { className: styles.toolCallArgs },
                React.createElement("pre", null, inputSummary))),
            showFileList && React.createElement(FileListView, { text: rawText, cwd: cwd }),
            displayContent && !showFileList && (React.createElement("div", { className: styles.toolCallCodeBlock },
                React.createElement("div", { className: styles.toolCallCodeBar },
                    React.createElement("div", { className: styles.toolCallTabRow }, canPreview ? (React.createElement(React.Fragment, null,
                        React.createElement("button", { className: `${styles.toolCallTab} ${viewMode === "code" ? styles.activeTab : ""}`, onClick: () => setViewMode("code") },
                            "</>",
                            " Code"),
                        React.createElement("button", { className: `${styles.toolCallTab} ${viewMode === "preview" ? styles.activeTab : ""}`, onClick: () => {
                                if (previewAccepted)
                                    setViewMode("preview");
                            }, disabled: !previewAccepted, title: !previewAccepted ? "Accept the warning below to enable preview" : "", style: !previewAccepted ? { opacity: 0.4, cursor: "default" } : undefined }, "\uD83D\uDC41 Preview"))) : (React.createElement("span", { className: styles.toolCallCodeLabel },
                        fileContent ? "Content" : "Output",
                        React.createElement("span", { className: styles.toolCallLineCount },
                            " \u00B7 ",
                            lineCount,
                            " lines")))),
                    React.createElement("div", { className: styles.toolCallBarActions },
                        viewMode === "code" && canPreview && (React.createElement("span", { className: styles.toolCallLineCountSmall },
                            lineCount,
                            " lines")),
                        viewMode === "code" && isLong && (React.createElement("button", { className: styles.toolCallToggleBtn, onClick: () => setIsExpanded(!isExpanded) }, isExpanded ? "Collapse ▲" : "Expand ▼")),
                        blobUrl && previewAccepted && (React.createElement("a", { href: blobUrl, target: "_blank", rel: "noopener noreferrer", className: styles.toolCallToggleBtn }, "Open in tab \u2197")))),
                canPreview && !previewAccepted && (React.createElement("div", { className: styles.toolCallPreviewBanner },
                    React.createElement("div", { className: styles.toolCallPreviewBannerText }, "\u26A0\uFE0F Preview executes code directly in your browser. If the agent generated malicious JavaScript, it will run outside of any guardrails."),
                    React.createElement("button", { className: styles.toolCallPreviewAcceptBtn, onClick: () => {
                            setPreviewAccepted(true);
                            setViewMode("preview");
                        } }, "I understand, enable preview"))),
                viewMode === "code" && (React.createElement("pre", { className: `${styles.toolCallCodePre} ${isExpanded ? styles.expanded : ""}` },
                    React.createElement(HighlightedCode, { code: isAibleToolCall && toolCall?.resultText ? toolCall.resultText : displayContent, language: prismLang }))),
                viewMode === "preview" && blobUrl && previewAccepted && (React.createElement("div", { className: styles.toolCallPreviewWrap },
                    React.createElement("iframe", { src: blobUrl, className: styles.toolCallPreviewIframe, sandbox: "allow-scripts allow-same-origin", title: `Preview: ${filePath}` }))))),
            fileContent && toolCall.output && (React.createElement("div", { className: styles.toolCallResult },
                React.createElement("pre", null, toolCall.output))),
            updateFeedback ? React.createElement(ToolCallFeedback, { feedback: feedback, updateFeedback: updateFeedback }) : null)));
};
export const ToolCallDisplay = ({ toolCall }) => {
    return React.createElement(ToolCallCard, { toolCall: toolCall });
};
export const ToolCallList = ({ toolCalls, toolCallsDisplayMode, toolCallsFeedback, updateToolCallsFeedbackByName }) => {
    if (!toolCalls || toolCalls.length === 0)
        return null;
    return (React.createElement(React.Fragment, null, toolCalls.map((tc, i) => {
        let feedback;
        let updateFeedback;
        if (tc.name) {
            feedback = toolCallsFeedback?.[tc.name] ?? "";
            updateFeedback = (val) => {
                updateToolCallsFeedbackByName(tc.name, val);
            };
        }
        return (React.createElement(ToolCallCard, { key: tc.id ?? i, toolCall: tc, toolCallsDisplayMode: toolCallsDisplayMode, feedback: feedback, updateFeedback: updateFeedback }));
    })));
};
export default ToolCallDisplay;
